import React from 'react';

import { client } from '~/api/client';
import {
  CaraWorkspaceUseCase,
  useUpdateAgencyUserPreferencesMutation,
  type AgencyUserHomePinInput
} from '~/api/graphql';

/** Long enough that a drag is one write; short enough that leaving the page still persists. */
const PERSIST_DELAY_MS = 600;

export type HomePin =
  | { kind: 'useCase'; id: CaraWorkspaceUseCase }
  | { kind: 'customPreset'; id: string };

export const pinKey = (pin: HomePin) => `${pin.kind}:${pin.id}`;

export const samePin = (a: HomePin, b: HomePin) => a.kind === b.kind && a.id === b.id;

const keyOf = (pins: HomePin[]) => pins.map(pinKey).join(',');

const toInput = (pin: HomePin): AgencyUserHomePinInput =>
  pin.kind === 'useCase' ? { useCase: pin.id } : { customPresetId: pin.id };

/**
 * What a profile says about this user's home screen. Absent for a user no
 * profile reaches, which leaves the tray behaving as it always has.
 */
export type AgencyProfileHomeScreenRules = {
  /** Presets an admin put on the home screen, in their order. */
  pins: HomePin[];
  /** False locks the arrangement to `pins` and stops all write-back. */
  allowUserCustomization: boolean;
};

/**
 * Local tray state with a trailing persist. Writes skip route revalidation: the
 * agency user comes from a deferred loader, so refetching remounts the page and
 * tears down a mid-arrange drawer.
 *
 * Empty is a real state — no default set is substituted, except the one a
 * profile supplies. Unresolvable pins are dropped on read and pruned on the
 * next write.
 */
export const usePinnedHomeItems = (
  persisted: HomePin[],
  /** Shared by the tray, drawer, and write-back so those three cannot disagree. */
  resolves: (pin: HomePin) => boolean,
  profile?: AgencyProfileHomeScreenRules
) => {
  const [updatePreferences] = useUpdateAgencyUserPreferencesMutation({ client });

  const locked = !!profile && !profile.allowUserCustomization;

  // A locked profile owns the arrangement outright. An unlocked one supplies the
  // starting set, which the user's own arrangement replaces the moment they make
  // one — so the admin's picks seed the page rather than fighting the user's.
  const profilePins = profile?.pins ?? [];
  const source = locked ? profilePins : persisted.length > 0 ? persisted : profilePins;

  // Keyed on contents: the loader returns a new array each run, and depending on
  // identity would restart this memo (and the effect below) in a loop.
  const persistedKey = keyOf(source);
  const arranged = React.useMemo(
    () => source.filter(resolves),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [persistedKey, resolves]
  );

  const [pinned, setPinned] = React.useState<HomePin[]>(arranged);

  const timerRef = React.useRef<ReturnType<typeof setTimeout>>();
  /** An edit the user has made that the debounce has not released yet. */
  const pendingRef = React.useRef<HomePin[]>();
  /** The arrangement to send next. Newer replaces older, since each is the whole list. */
  const queuedRef = React.useRef<HomePin[]>();
  /** True while a mutation is outstanding. */
  const sendingRef = React.useRef(false);
  /** `keyOf` the arrangement last handed to the server, for the guard below. */
  const sentKeyRef = React.useRef<string>();

  // Ignore the loader while a local edit is unsent or the server has not caught up;
  // taking its copy in that window made a just-added pin vanish.
  React.useEffect(() => {
    if (pendingRef.current !== undefined || queuedRef.current !== undefined) {
      return;
    }
    if (sentKeyRef.current !== undefined && sentKeyRef.current !== persistedKey) {
      return;
    }
    sentKeyRef.current = undefined;
    setPinned(arranged);
  }, [arranged, persistedKey]);

  /**
   * Drains the queue one request at a time.
   *
   * Every write carries the whole arrangement, so two in flight at once is a race to
   * be stored last: a slow early request landing after a fast later one leaves the
   * server holding the older list, with the newer one still on screen and nothing to
   * correct it. Sending them in order removes the race, and a newer arrangement
   * replaces a waiting one outright because there is no value in sending both.
   */
  const drain = React.useCallback(async () => {
    if (sendingRef.current) {
      return;
    }
    sendingRef.current = true;
    try {
      while (queuedRef.current !== undefined) {
        const next = queuedRef.current;
        queuedRef.current = undefined;
        sentKeyRef.current = keyOf(next);
        try {
          await updatePreferences({ variables: { input: { homePins: next.map(toInput) } } });
        } catch {
          // Leave the on-screen order alone. Reverting would discard an arrangement
          // the user can see for a message they did not ask for, and the next edit
          // sends the whole list again.
          sentKeyRef.current = undefined;
        }
      }
    } finally {
      sendingRef.current = false;
    }
  }, [updatePreferences]);

  const write = React.useCallback(
    (next: HomePin[]) => {
      pendingRef.current = undefined;
      queuedRef.current = next;
      void drain();
    },
    [drain]
  );

  const persist = React.useCallback(
    (next: HomePin[]) => {
      // Nothing to write back when the profile owns the arrangement; the tray's
      // controls are hidden too, so this only guards a stray programmatic edit.
      if (locked) {
        return;
      }
      pendingRef.current = next;
      clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => write(next), PERSIST_DELAY_MS);
    },
    [locked, write]
  );

  // Flush on unmount so a pin-then-navigate still persists. It joins the same queue,
  // so it cannot overtake a request already on its way.
  React.useEffect(
    () => () => {
      clearTimeout(timerRef.current);
      if (pendingRef.current) {
        queuedRef.current = pendingRef.current;
        pendingRef.current = undefined;
        void drain();
      }
    },
    [drain]
  );

  const apply = React.useCallback(
    (next: HomePin[]) => {
      if (locked) {
        return;
      }
      setPinned(next);
      persist(next);
    },
    [locked, persist]
  );

  const isPinned = React.useCallback(
    (pin: HomePin) => pinned.some((p) => samePin(p, pin)),
    [pinned]
  );

  const toggle = React.useCallback(
    (pin: HomePin) =>
      apply(
        pinned.some((p) => samePin(p, pin))
          ? pinned.filter((p) => !samePin(p, pin))
          : [...pinned, pin]
      ),
    [apply, pinned]
  );

  const remove = React.useCallback(
    (pin: HomePin) => apply(pinned.filter((p) => !samePin(p, pin))),
    [apply, pinned]
  );

  // One call: looping `toggle` reads the same `pinned` each time, so the last wins.
  const pinAll = React.useCallback(
    (incoming: HomePin[]) => {
      const fresh = incoming.filter((pin) => !pinned.some((p) => samePin(p, pin)));
      if (fresh.length > 0) {
        apply([...pinned, ...fresh]);
      }
    },
    [apply, pinned]
  );

  return { pinned, isPinned, toggle, remove, pinAll, reorder: apply, locked };
};
